import { createTwoFilesPatch, FILE_HEADERS_ONLY } from 'diff'

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Pick a key to sort a list of ref-like objects by, so array order doesn't
 * depend on DB/query order (e.g. connection operations, skill/knowledge-source
 * refs, access permissions — all shaped as `{ someId, ...fields }`). Only a
 * key that is present, comparable, and unique across every item qualifies,
 * so this never reorders by a field that could itself be the changed value.
 */
function findStableSortKey(items: Record<string, unknown>[]): string | null {
  if (items.length === 0) return null
  const candidateKeys = Object.keys(items[0]).filter((key) => key === 'id' || key.endsWith('Id'))
  for (const key of candidateKeys) {
    const values = items.map((item) => item[key])
    const comparable = values.every((v) => typeof v === 'string' || typeof v === 'number')
    const unique = new Set(values).size === values.length
    if (comparable && unique) return key
  }
  return null
}

/**
 * Recursively sort arrays of ref-like objects by a stable identity key before
 * diffing. Audit before/after states are independently snapshotted (a fresh
 * DB read vs. a frontend-submitted payload with newly-toggled items appended
 * at the tail), so the same underlying set of items can come back in a
 * different order with no actual content change. Left un-normalized, the
 * line-based unified diff below would show that as a large spurious
 * remove/add patch instead of the one real field change. Arrays of
 * primitives (e.g. tags) are left as-is since their order can be meaningful.
 */
function normalizeArrayOrderForDiff(value: unknown): unknown {
  if (Array.isArray(value)) {
    const items = value.map(normalizeArrayOrderForDiff)
    if (items.every(isPlainObject)) {
      const sortKey = findStableSortKey(items)
      if (sortKey) {
        return [...items].sort((a, b) =>
          String(a[sortKey]).localeCompare(String(b[sortKey]))
        )
      }
    }
    return items
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, v]) => [key, normalizeArrayOrderForDiff(v)]))
  }
  return value
}

/**
 * Pretty-print a before/after audit state for diffing.
 *
 * `JSON.stringify` never emits the literal two-character sequence `\n` (or
 * `\t`/`\r`) outside an escaped string body, so unescaping those sequences
 * back into real line breaks is safe and turns a long markdown-in-JSON field
 * (e.g. an Agent DNA "Purpose" or a skill.md body) into many real lines
 * instead of one enormous one — that's what lets the unified-diff context
 * radius collapse/expand unchanged spans *inside* a single large field, not
 * just between sibling fields. The output is intentionally not valid JSON if
 * unescaped this way; it's display-only text and is never re-parsed.
 */
export function prettyPrintStateForDiff(state: Record<string, unknown> | null): string {
  if (state === null) return ''
  const normalized = normalizeArrayOrderForDiff(state) as Record<string, unknown>
  return JSON.stringify(normalized, null, 2).replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\r/g, '\r')
}

/**
 * Build a unified-diff text block between two already pretty-printed text
 * blobs, with a fixed context-line radius (default 3, the git convention).
 * Filenames are structural placeholders — never surfaced in the UI.
 *
 * `react-diff-view`'s `parseDiff` is backed by `gitdiff-parser`, which only
 * recognizes a file boundary via a `diff --git a/x b/x` header line — a
 * plain jsdiff unified patch has no such line, so one is prepended here.
 */
export function buildUnifiedDiffText(beforeText: string, afterText: string, contextLines = 3): string {
  const patch = createTwoFilesPatch('before', 'after', beforeText, afterText, undefined, undefined, {
    context: contextLines,
    headerOptions: FILE_HEADERS_ONLY,
  })
  return `diff --git a/before b/after\n${patch}`
}
